import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/logo";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

export function MainNav() {
  // Create a state to track if we're in a Router context
  const [isActive, setIsActive] = useState<Record<string, boolean>>({
    "/": true,
    "/features": false,
    "/pricing": false,
    "/examples": false
  });
  const [isRouterAvailable, setIsRouterAvailable] = useState(true);
  
  // Try to use useLocation safely with try-catch
  let location: { pathname: string } = { pathname: "/" };
  
  try {
    // This will throw if not in router context
    location = useLocation();
    
    // If we get here, router is available
    useEffect(() => {
      setIsRouterAvailable(true);
      setIsActive({
        "/": location.pathname === "/",
        "/features": location.pathname === "/features",
        "/pricing": location.pathname === "/pricing",
        "/examples": location.pathname === "/examples"
      });
    }, [location.pathname]);
  } catch (error) {
    // If we catch an error, router is not available
    if (isRouterAvailable) {
      console.error("MainNav: Router context not available", error);
      setIsRouterAvailable(false);
    }
  }
  
  const NavLink = ({ href, children }: { href: string; children: React.ReactNode }) => {
    // If router is not available, fall back to regular a tags
    if (!isRouterAvailable) {
      return (
        <a 
          href={href} 
          className={cn(
            "text-sm font-medium transition-colors hover:text-primary",
            isActive[href] 
              ? "text-primary font-semibold" 
              : "text-muted-foreground"
          )}
        >
          {children}
        </a>
      );
    }
    
    return (
      <Link 
        to={href} 
        className={cn(
          "text-sm font-medium transition-colors hover:text-primary",
          isActive[href] 
            ? "text-primary font-semibold" 
            : "text-muted-foreground"
        )}
      >
        {children}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center gap-8">
          {isRouterAvailable ? (
            <Link to="/">
              <Logo />
            </Link>
          ) : (
            <a href="/">
              <Logo />
            </a>
          )}
          <nav className="hidden md:flex gap-6">
            <NavLink href="/features">Features</NavLink>
            <NavLink href="/pricing">Pricing</NavLink>
            <NavLink href="/examples">Examples</NavLink>
          </nav>
        </div>
        <div className="flex items-center gap-4">
          {isRouterAvailable ? (
            <>
              <Link to="/login">
                <Button variant="ghost" className="hidden sm:flex">
                  Log in
                </Button>
              </Link>
              <Link to="/signup">
                <Button>Start for free</Button>
              </Link>
            </>
          ) : (
            <>
              <a href="/login">
                <Button variant="ghost" className="hidden sm:flex">
                  Log in
                </Button>
              </a>
              <a href="/signup">
                <Button>Start for free</Button>
              </a>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
